import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { STRINGS } from '../constants/strings'
import { useAuth } from '../hooks'
import Logo from './logo'
import ThemeToggle from './themeToggle'
import HelpButton from './helpButton'


function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const ref = useRef(null)

  const initials = user.name
    ? user.name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'
    : (user.email?.[0] ?? '?').toUpperCase()

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  return (
    <div ref={ref} className="relative">
      {/* Trigger pill */}
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 p-0 sm:px-2.5 sm:py-1.5 rounded-xl sm:border transition-all"
        style={{
          background:   'var(--surface)',
          borderColor:  open ? 'var(--brand)' : 'var(--border)',
          color:        'var(--text-1)',
        }}
      >
        {/* Avatar */}
        {user.profilePic ? (
          <img src={user.profilePic} alt="" className="w-9 h-9 rounded-xl object-cover flex-shrink-0" />
        ) : (
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold flex-shrink-0"
               style={{ background: 'var(--brand)', color: '#fff' }}>
            {initials}
          </div>
        )}
        <span className="text-xs font-medium hidden sm:block max-w-[100px] truncate">
          {user.name || user.email}
        </span>
        {/* Chevron — desktop only */}
        <svg className={`w-3 h-3 flex-shrink-0 transition-transform hidden sm:block ${open ? 'rotate-180' : ''}`}
             style={{ color: 'var(--text-3)' }}
             viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-56 rounded-2xl border shadow-2xl overflow-hidden animate-fade-up z-50"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        >
          {/* User info */}
          <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-3">
              {user.profilePic ? (
                <img src={user.profilePic} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                     style={{ background: 'var(--brand)', color: '#fff' }}>
                  {initials}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>
                  {user.name || 'User'}
                </p>
                <p className="text-xs truncate" style={{ color: 'var(--text-3)' }}>
                  {user.email}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="p-1.5">
            <button
              onClick={() => { setOpen(false); navigate('/profile') }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all text-left"
              style={{ color: 'var(--text-2)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--text-1)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)' }}
            >
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 16 16" fill="none"
                   stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/>
              </svg>
              Profile
            </button>
            <button
              onClick={() => { setOpen(false); onLogout() }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all text-left"
              style={{ color: 'var(--text-2)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.08)'; e.currentTarget.style.color = '#f87171' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)' }}
            >
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 16 16" fill="none"
                   stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 2h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-3M6 11l-3-3 3-3M2 8h8" />
              </svg>
              {STRINGS.auth.logout}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function NavBar() {
  const { pathname }     = useLocation()
  const { user, logout } = useAuth()
  const navigate         = useNavigate()

  function handleLogout() {
    logout()
    navigate('/login')
  }

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl transition-colors"
      style={{
        borderBottom: pathname === '/' ? 'none' : '1px solid var(--border)',
        background: pathname === '/' ? 'transparent' : 'color-mix(in srgb, var(--bg) 80%, transparent)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 w-full h-14 grid grid-cols-[auto_1fr_auto] items-center gap-3">
        {/* Logo */}
        <Link to={user?.role === 'superadmin' ? '/superadmin' : user?.role === 'organisation' ? '/admin' : '/'} className="flex-shrink-0">
          <Logo size="nav" />
        </Link>

        {/* Dashboard label — gradient text, no pill/background. Sits in the middle
            grid column, which only ever gets the space left between the logo
            and the right-side controls, so it can truncate but never overlap.
            Shown at every width now — short wording below lg, where the nav
            pills/controls leave much less room, full wording at lg+. */}
        <div className="flex justify-center min-w-0 overflow-hidden">
          {user && (user.role === 'organisation' || user.role === 'user') && (
            <span className="flex items-center gap-1 sm:gap-1.5 min-w-0">
              {/* Plain, not gradient-filled — emoji glyphs don't render through
                  a transparent-text/background-clip trick, they'd just vanish. */}
              <span className="text-xs sm:text-sm flex-shrink-0">{user.role === 'organisation' ? '🏢' : '🎓'}</span>
              <span
                className="text-xs sm:text-sm font-bold truncate"
                style={{
                  backgroundImage: 'linear-gradient(90deg, var(--brand), color-mix(in srgb, var(--brand) 55%, #818cf8))',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '0.01em',
                }}
              >
                <span className="lg:hidden">{user.role === 'organisation' ? 'Dashboard - Org' : 'Dashboard - Examinee'}</span>
                <span className="hidden lg:inline">{user.role === 'organisation' ? 'Dashboard - Organisation / Institution' : 'Dashboard - Examinee / Student'}</span>
              </span>
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 justify-self-end">
          {/* Nav pills — hidden when logged out, and for superadmin (only one destination, the logo already goes there) */}
          {user && user.role !== 'superadmin' && (
            <nav className="flex items-center gap-0.5 sm:gap-1 rounded-xl p-1"
                 style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              {(user?.role === 'organisation'
                ? [
                    { to: '/admin',   icon: '⚙️', label: STRINGS.nav.manage },
                    { to: '/',        icon: '🎯', label: STRINGS.nav.exam },
                    { to: '/history', icon: '📋', label: 'History' },
                  ]
                : [
                    { to: '/',        icon: '🎯', label: STRINGS.nav.exam },
                    { to: '/history', icon: '📋', label: 'History' },
                  ]
              ).map(({ to, icon, label }) => (
                <Link key={to} to={to}
                  className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={pathname === to ? { background: 'var(--brand)', color: '#fff' } : { color: 'var(--text-2)' }}
                  onMouseEnter={e => { if (pathname !== to) { e.currentTarget.style.color = 'var(--text-1)'; e.currentTarget.style.background = 'var(--surface-2)' } }}
                  onMouseLeave={e => { if (pathname !== to) { e.currentTarget.style.color = 'var(--text-2)'; e.currentTarget.style.background = '' } }}
                >
                  <span>{icon}</span>
                  <span className="hidden sm:inline">{label}</span>
                </Link>
              ))}
            </nav>
          )}

          {user?.role !== 'superadmin' && <HelpButton />}

          <ThemeToggle />

          {user && <UserMenu user={user} onLogout={handleLogout} />}
        </div>
      </div>
    </header>
  )
}
